import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Bell, Monitor, Smartphone, Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { API, getAuth } from './taskConfigs';
import { initPushNotifications, isPushSupported, isPushPermissionGranted } from '../../utils/pushNotifications';

export const NotificationSettingsPanel = ({ user, onClose }) => {
  const [prefs, setPrefs] = useState({
    desktop_notifications: true,
    mobile_push: true,
    email_digest: true,
    task_created: true,
    task_updated: true,
    task_completed: true,
    task_reminder: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState('checking'); // checking, active, inactive, unsupported

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const { data } = await axios.get(`${API}/api/tasks/notification-preferences`, getAuth());
        if (data.preferences) setPrefs(p => ({ ...p, ...data.preferences }));
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetchPrefs();

    // Check push status
    if (!isPushSupported()) {
      setPushStatus('unsupported');
    } else if (isPushPermissionGranted()) {
      setPushStatus('active');
    } else {
      setPushStatus('inactive');
    }
  }, []);

  const handleEnablePush = async () => {
    setPushStatus('checking');
    const ok = await initPushNotifications();
    setPushStatus(ok ? 'active' : 'inactive');
    if (ok) toast.success('Push notifications enabled!');
    else toast.error('Could not enable push notifications. Please allow notifications in browser settings.');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/tasks/notification-preferences`, { preferences: prefs }, getAuth());
      toast.success('Notification preferences saved');
      onClose();
    } catch { toast.error('Failed to save preferences'); }
    setSaving(false);
  };

  const Toggle = ({ label, icon: Icon, checked, onChange, testId }) => (
    <div className="flex items-center justify-between py-3 px-1">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-gray-300'}`}
        data-testid={testId}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-sm h-full shadow-2xl overflow-y-auto animate-slideInRight" onClick={e => e.stopPropagation()} data-testid="notif-settings-panel">
        <div className="sticky top-0 text-white px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }}>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <h2 className="font-semibold text-base">Notification Settings</h2>
          </div>
          <button onClick={onClose} data-testid="notif-settings-close"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Push Notification Status */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Push Status</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                {pushStatus === 'active' && (
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-green-700">Push notifications active</p>
                      <p className="text-xs text-gray-500">You'll receive reminders in Windows Notification Center / Mobile</p>
                    </div>
                  </div>
                )}
                {pushStatus === 'inactive' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      <p className="text-sm text-amber-700">Push notifications are not enabled</p>
                    </div>
                    <button onClick={handleEnablePush} className="w-full py-2 text-white rounded-xl text-sm font-medium shadow-[0_4px_10px_rgba(217,119,6,0.30)] hover:shadow-[0_6px_14px_rgba(217,119,6,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }} data-testid="enable-push-btn">
                      Enable Push Notifications
                    </button>
                  </div>
                )}
                {pushStatus === 'unsupported' && (
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                    <p className="text-sm text-gray-500">Push notifications not supported in this browser</p>
                  </div>
                )}
                {pushStatus === 'checking' && (
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <p className="text-sm text-gray-500">Checking push status...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Channels */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Channels</h3>
              <div className="bg-gray-50 rounded-xl p-3 divide-y divide-gray-200">
                <Toggle label="Desktop Notifications" icon={Monitor} checked={prefs.desktop_notifications} onChange={v => setPrefs(p => ({ ...p, desktop_notifications: v }))} testId="toggle-desktop" />
                <Toggle label="Mobile Push" icon={Smartphone} checked={prefs.mobile_push} onChange={v => setPrefs(p => ({ ...p, mobile_push: v }))} testId="toggle-mobile" />
                <Toggle label="Email Digest" icon={Mail} checked={prefs.email_digest} onChange={v => setPrefs(p => ({ ...p, email_digest: v }))} testId="toggle-email" />
              </div>
            </div>

            {/* Event Types */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Events</h3>
              <div className="bg-gray-50 rounded-xl p-3 divide-y divide-gray-200">
                <Toggle label="Task Created" icon={Bell} checked={prefs.task_created} onChange={v => setPrefs(p => ({ ...p, task_created: v }))} testId="toggle-task-created" />
                <Toggle label="Task Updated" icon={Bell} checked={prefs.task_updated} onChange={v => setPrefs(p => ({ ...p, task_updated: v }))} testId="toggle-task-updated" />
                <Toggle label="Task Completed" icon={Bell} checked={prefs.task_completed} onChange={v => setPrefs(p => ({ ...p, task_completed: v }))} testId="toggle-task-completed" />
                <Toggle label="Task Reminders" icon={Bell} checked={prefs.task_reminder} onChange={v => setPrefs(p => ({ ...p, task_reminder: v }))} testId="toggle-task-reminder" />
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} className="w-full py-2.5 text-white rounded-xl text-sm font-medium shadow-[0_4px_12px_rgba(217,119,6,0.30)] hover:shadow-[0_8px_20px_rgba(217,119,6,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)' }} data-testid="save-notif-prefs">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
